import { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router';
import Header from './inbox/header/Header';
import TaskDataManager from './inbox/TaskDataManager';
import Login from './login/Login';
import type { JwtToken } from './types/global';

const AppRoot = () => {
    const [jwt, setJwt] = useState<JwtToken | null>(null);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (!jwt && location.pathname !== '/login') {
            // Store the current URL for redirect after login (properly encode the full URL)
            const currentUrl = location.pathname + location.search;
            navigate(`/login?redirectUrl=${encodeURIComponent(currentUrl)}`);
        }
    }, [jwt, navigate, location]);

    const handleLoginSuccess = (token: JwtToken) => {
        setJwt(token);
        // Properly decode the redirect URL to preserve query parameters
        const redirectUrl = new URLSearchParams(location.search).get('redirectUrl');
        const decodedUrl = redirectUrl ? decodeURIComponent(redirectUrl) : '/';
        navigate(decodedUrl);
    };

    return (
        <Routes>
            <Route path="/login" element={<Login handleLoginSuccess={handleLoginSuccess} />} />
            <Route
                path="/"
                element={
                    <ProtectedRoute jwt={jwt}>
                        <TaskDataManager jwt={jwt!} />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/task/:taskId"
                element={
                    <ProtectedRoute jwt={jwt}>
                        <TaskDataManager jwt={jwt!} />
                    </ProtectedRoute>
                }
            />
            <Route path="*" element={<NotFoundPage />} />
        </Routes>
    );
};

const ProtectedRoute = ({ jwt, children }: { jwt: JwtToken | null; children: React.ReactNode }) => {
    if (!jwt) {
        return null; // Will be redirected by useEffect in AppRoot
    }

    return (
        <div className="layout">
            <Header jwt={jwt} />
            {children}
        </div>
    );
};

const NotFoundPage = () => {
    return (
        <div className="not-found-page">
            <h2>Page Not Found</h2>
            <p>The page you're looking for doesn't exist.</p>
            <Link to="/">Go back to tasks</Link>
        </div>
    );
};

export default AppRoot;
